
import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import { Lesson } from "@/types/supabase";
import { markLessonAsCompleted } from "@/lib/user-courses";

/**
 * Hook to manage active lesson content (video and PDF) and completion tracking
 */
export function useLessonContent(
  lessons: Lesson[], 
  userCourse: any | null
) {
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [activePdfUrl, setActivePdfUrl] = useState<string | null>(null);
  const [activeLesson, setActiveLesson] = useState<any | null>(null);
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);

  // Set initial lesson content based on last viewed or first lesson
  useEffect(() => {
    if (lessons.length > 0 && userCourse) {
      let lessonToUse = null;
      
      // First try to use the last viewed lesson if available
      if (userCourse.last_lesson_id) {
        const lastLesson = lessons.find(lesson => lesson.id === userCourse.last_lesson_id);
        if (lastLesson) {
          console.log("Found last viewed lesson:", lastLesson);
          lessonToUse = lastLesson;
        }
      }
      
      // If no last lesson was found, use the first lesson
      if (!lessonToUse) {
        console.log("Using first lesson as default:", lessons[0]);
        lessonToUse = lessons[0];
      }
      
      // Set video and PDF content if available
      if (lessonToUse) {
        if (lessonToUse.video_id) {
          console.log("Setting video ID:", lessonToUse.video_id);
          setActiveVideoId(lessonToUse.video_id);
        }
        
        if (lessonToUse.pdf_url) {
          console.log("Setting PDF URL:", lessonToUse.pdf_url);
          setActivePdfUrl(lessonToUse.pdf_url);
        }
        
        setActiveLesson(lessonToUse);
      }
    }
  }, [lessons, userCourse]);

  // Handle lesson selection
  const handleLessonSelect = (lesson: any) => {
    console.log("Selected lesson:", lesson);
    
    if (!lesson.video_id && !lesson.pdf_url) {
      toast({
        title: "Lesson Unavailable",
        description: "This lesson doesn't have any content available yet.",
        variant: "destructive"
      });
      return;
    }
    
    // Update the active content
    if (lesson.video_id) {
      console.log("Setting active video ID:", lesson.video_id);
      setActiveVideoId(lesson.video_id);
    }
    
    if (lesson.pdf_url) {
      console.log("Setting active PDF URL:", lesson.pdf_url);
      setActivePdfUrl(lesson.pdf_url);
    }
    
    setActiveLesson(lesson);
    
    toast({
      title: "Loading Lesson",
      description: `Loading: ${lesson.title}`,
    });
  };

  // Handle video completion
  const handleLessonComplete = async () => {
    if (!userCourse || !activeLesson) {
      console.error("Cannot complete lesson: userCourse or activeLesson is null");
      return;
    }
    
    try {
      setIsUpdatingProgress(true);
      console.log("Marking lesson as completed:", activeLesson.id);
      
      const updatedUserCourse = await markLessonAsCompleted(
        userCourse.id,
        activeLesson.id,
        userCourse.course_id
      );
      
      if (updatedUserCourse) {
        toast({
          title: "Lesson Completed",
          description: "Your progress has been updated.",
        });
        console.log("Progress updated:", updatedUserCourse);
        
        // Return the updated user course for the parent component to use
        return updatedUserCourse;
      } else {
        throw new Error("Failed to update progress");
      }
    } catch (error) {
      console.error("Error completing lesson:", error);
      toast({
        title: "Error",
        description: "Failed to update your progress. Please try again.",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  // Handle video end event
  const handleVideoEnd = async () => {
    console.log("Video ended, marking lesson as completed");
    return handleLessonComplete();
  };

  return {
    activeVideoId,
    activePdfUrl,
    activeLesson,
    handleLessonSelect,
    handleVideoEnd,
    handleLessonComplete,
    isUpdatingProgress
  };
}
